<script setup>
import { computed, ref, watch } from 'vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import DurationInput from 'dashboard/components-next/input/DurationInput.vue';
import { DURATION_UNITS } from 'dashboard/components-next/input/constants';

import KraftchatAIRulesSwitchItem from './KraftchatAIRulesSwitchItem.vue';

import { rulesContent } from '../../helpers/kraftchatAIRulesConfig';

const props = defineProps({
  policy: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['update:policy']);

// Only ever suggested when the user turns the feature ON, never persisted
// on its own - matches the backend contract where nil means "handoff only
// ends manually".
const SUGGESTED_HANDOFF_TIMEOUT_MINUTES = 120;

const updateField = (key, value) => {
  emit('update:policy', {
    ...props.policy,
    [key]: value,
  });
};

const handoffTimeoutEnabled = computed(
  () => props.policy.handoff_timeout_minutes != null
);

const durationUnit = ref(DURATION_UNITS.MINUTES);
watch(
  () => props.policy.handoff_timeout_minutes,
  minutes => {
    if (!minutes) return;
    durationUnit.value =
      minutes % 60 === 0 ? DURATION_UNITS.HOURS : DURATION_UNITS.MINUTES;
  },
  { immediate: true }
);

const toggleHandoffTimeout = enabled => {
  updateField(
    'handoff_timeout_minutes',
    enabled ? SUGGESTED_HANDOFF_TIMEOUT_MINUTES : null
  );
};
</script>

<template>
  <div class="grid gap-4 md:grid-cols-2">
    <KraftchatAIRulesSwitchItem
      :label="rulesContent.handoffEnabledLabel"
      :description="rulesContent.handoffEnabledDescription"
      :model-value="policy.human_handoff_enabled"
      @update:model-value="updateField('human_handoff_enabled', $event)"
    />

    <TextArea
      :model-value="policy.handoff_message"
      :label="rulesContent.handoffMessageLabel"
      :placeholder="rulesContent.handoffMessagePlaceholder"
      min-height="7rem"
      resize
      @update:model-value="updateField('handoff_message', $event)"
    />

    <div class="md:col-span-2 flex flex-col gap-3">
      <KraftchatAIRulesSwitchItem
        :label="rulesContent.handoffTimeoutEnabledLabel"
        :description="rulesContent.handoffTimeoutEnabledDescription"
        :model-value="handoffTimeoutEnabled"
        @update:model-value="toggleHandoffTimeout"
      />

      <div
        v-if="handoffTimeoutEnabled"
        class="flex items-center gap-2 rounded-lg border border-n-weak bg-n-solid-1 p-4"
      >
        <span class="text-sm text-n-slate-11 flex-shrink-0">
          {{ rulesContent.handoffTimeoutLabel }}
        </span>
        <DurationInput
          :model-value="policy.handoff_timeout_minutes"
          :unit="durationUnit"
          :min="1"
          class="max-w-xs"
          @update:model-value="updateField('handoff_timeout_minutes', $event)"
          @update:unit="durationUnit = $event"
        />
      </div>
    </div>
  </div>
</template>
